import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Button as MuiButton,
  TextField,
  Stack,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { axiosRubyClient } from '@/util/axios/axiosClient';

interface RemoveFromAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  workerFullName: string;
  workerInfo?: any;
  job?: any;
  jobWorkerId?: string;
}

const RemoveFromAssignmentModal: React.FC<RemoveFromAssignmentModalProps> = ({
  open,
  onClose,
  workerFullName,
  workerInfo,
  job,
  jobWorkerId,
}) => {
  const [reason, setReason] = useState<string>('');
  const [showError, setShowError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Reset state when modal opens/closes
    if (!open) {
      setReason('');
      setShowError(false);
      setIsSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    // Reset error when reason is entered
    if (reason) {
      setShowError(false);
    }
  }, [reason]);

  const handleConfirm = async () => {
    if (isSubmitting) return;

    if (!reason.trim()) {
      setShowError(true);
      return;
    }

    if (!jobWorkerId) {
      console.error('Job Worker ID not found');
      return;
    }

    setIsSubmitting(true);
    try {
      await axiosRubyClient.post(
        '/api/admin/job_workers/remove_lta_assignment',
        {
          reason: reason.trim(),
          jobWorkerId: jobWorkerId,
        },
      );

      onClose();
      window.location.reload();
    } catch (error) {
      console.error('Error removing worker from assignment:', error);
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isSubmitting}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: '24px',
          touchAction: 'manipulation',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            touchAction: 'manipulation',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          padding: 0,
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            color: '#1C1C1C',
            fontFamily: 'Manrope',
            fontSize: '20px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: '28px',
          }}
        >
          Remove from Assignment
        </Typography>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            if (!isSubmitting) {
              handleClose();
            }
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (!isSubmitting) {
              handleClose();
            }
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (!isSubmitting) {
              handleClose();
            }
          }}
          disabled={isSubmitting}
          sx={{
            padding: 0,
            color: '#1C1C1C',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: 0 }}>
        <Stack spacing={2}>
          {/* Main Message */}
          <Typography
            sx={{
              color: '#1C1C1C',
              fontFamily: 'Manrope',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: '22.4px',
            }}
          >
            You're about to remove{' '}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {workerFullName}
            </Box>{' '}
            from this assignment.
          </Typography>

          {/* Instruction */}
          <Typography
            sx={{
              color: '#5B5B5B',
              fontFamily: 'Manrope',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '22.4px',
            }}
          >
            Add a reason to proceed with removal.
          </Typography>

          {/* Reason Text Area */}
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Reason for dismissal (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            error={showError}
            helperText={showError ? 'Please provide a reason for removal' : ''}
            disabled={isSubmitting}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontFamily: 'Manrope',
              },
            }}
          />

          {/* Action Buttons */}
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            sx={{ marginTop: '24px' }}
          >
            <MuiButton
              onClick={(e) => {
                e.stopPropagation();
                if (!isSubmitting) {
                  handleClose();
                }
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (!isSubmitting) {
                  handleClose();
                }
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (!isSubmitting) {
                  handleClose();
                }
              }}
              disabled={isSubmitting}
              sx={{
                display: 'flex',
                width: '160px',
                height: '32px',
                padding: '0 16px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '12px',
                borderRadius: '4px',
                border: '1px solid #45A735',
                background: '#FFF',
                color: '#2E8722',
                textAlign: 'center',
                fontFamily: 'Manrope',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '16.8px',
                textTransform: 'none',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
                '&:hover': {
                  background: '#F5F5F5',
                  border: '1px solid #45A735',
                },
              }}
            >
              Cancel
            </MuiButton>
            <MuiButton
              onClick={(e) => {
                e.stopPropagation();
                if (!isSubmitting && reason.trim()) {
                  handleConfirm();
                }
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (!isSubmitting && reason.trim()) {
                  handleConfirm();
                }
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (!isSubmitting && reason.trim()) {
                  handleConfirm();
                }
              }}
              disabled={!reason.trim() || isSubmitting}
              sx={{
                display: 'flex',
                width: '160px',
                height: '32px',
                padding: '0 16px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '12px',
                borderRadius: '4px',
                background:
                  !reason.trim() || isSubmitting ? '#9E9E9E' : '#45A735',
                boxShadow:
                  !reason.trim() || isSubmitting
                    ? 'none'
                    : '0 2px 4px 0 rgba(0, 0, 0, 0.12)',
                color: '#FFF',
                textAlign: 'center',
                fontFamily: 'Manrope',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '16.8px',
                textTransform: 'none',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
                '&:hover': {
                  background:
                    !reason.trim() || isSubmitting ? '#9E9E9E' : '#2E8722',
                },
              }}
            >
              Confirm
            </MuiButton>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default RemoveFromAssignmentModal;
